import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Clock,
  FileText,
  CheckCircle,
  Scale,
  Handshake,
  DollarSign,
  MessageCircle,
  Ban,
  Copyright,
  AlertTriangle,
  Zap,
  Mail
} from "lucide-react";

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
          {/* Header */}
        <header className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl mb-6 shadow-xl">
              <Scale className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Terms and Conditions
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Please read these terms and conditions carefully before using our service.
            </p>
            <Badge variant="secondary" className="mt-4 bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm font-medium">
              <Clock className="w-4 h-4 mr-2" />
              Effective Date: {formattedDate}
            </Badge>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-12 space-y-8">
          {/* Introduction */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900 flex items-center">
                <Handshake className="w-8 h-8 text-blue-600 mr-3" />
                Accepting of Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Master Subscription Agreement (this "Agreement") contains terms and conditions that govern your purchase of subscriptions to, and use of, the Services (as defined in Section 2 below), and is a contract between Timelyyy Technologies ("Timelyyy"), and you or the entity or organization that you represent.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Timelyyy (Hereinafter Referred to as "Platform", "Mobile Application", "Website", "Timelyyy") is a Vehicle Wash Booking platform owned and operated by Timelyyy Technologies (Hereinafter Referred to as "Timelyyy", "we", "us", or "our").
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                These Terms of Use (hereinafter referred to as "Terms") govern the use of our website located at https://www.timelyyy.com (the "Service") operated by Timelyyy Technologies ("us", "we", or "our").
              </p>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, then you may not access the Service.
              </p>
            </CardContent>
          </Card>

          {/* Definitions */}
          <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileText className="text-blue-600 mr-2" />
              Definitions
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  term: "Account",
                  description: "means a unique account created for You to access Our Service or parts of Our Service.",
                  icon: CheckCircle,
                },
                {
                  term: "Affiliate",
                  description: "means an entity that controls, is controlled by or is under common control with a party, where 'control' means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.",
                  icon: CheckCircle,
                },
                {
                  term: "Application",
                  description: "means the software program provided by the Company downloaded by You on any electronic device, named Timelyyy.",
                  icon: CheckCircle,
                },
                {
                  term: "Company",
                  description: "refers to Timelyyy Technologies, 123, ABC Road, Mumbai, India.",
                  icon: CheckCircle,
                },
                {
                  term: "Cookies",
                  description: "are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.",
                  icon: CheckCircle,
                },
                {
                  term: "Country",
                  description: "refers to India.",
                  icon: Shield,
                },
                {
                  term: "Device",
                  description: "means any device that can access the Service such as a computer, a cellphone or a digital tablet.",
                  icon: Clock,
                },
                {
                  term: "Personal Data",
                  description: "is any information that relates to an identified or identifiable individual.",
                  icon: CheckCircle,
                },
                {
                  term: "Service",
                  description: "refers to the Website/Application.",
                  icon: CheckCircle,
                },
                {
                  term: "Service Provider",
                  description: "means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.",
                  icon: CheckCircle,
                },
                {
                  term: "Third-party Social Media Service",
                  description: "refers to any website or any social network website by which a User can log in or create an account to use the Service.",
                  icon: CheckCircle,
                },
                {
                  term: "Usage Data",
                  description: "refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).",
                  icon: CheckCircle,
                },
                {
                  term: "Website",
                  description: "refers to Timelyyy, accessible from https://www.timelyyy.com",
                  icon: Shield,
                },
                {
                  term: "You",
                  description: "means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.",
                  icon: CheckCircle,
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <dt className="font-semibold text-gray-900">{item.term}</dt>
                    <dd className="text-sm text-gray-600 mt-1">{item.description}</dd>
                  </div>
                </div>
              ))}
                </div>
          </section>

          {/* Payment Terms */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <DollarSign className="w-6 h-6 text-green-600 mr-3" />
                Payment Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                All payments for services are due in advance. We accept various payment methods including credit cards, debit cards, and digital wallets.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Refunds are subject to our refund policy. Please refer to our Refund Policy for detailed information.
              </p>
              <p className="text-gray-600 leading-relaxed">
                In case of failed payments, we reserve the right to suspend or terminate your account.
              </p>
            </CardContent>
          </Card>

          {/* Communication */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <MessageCircle className="w-6 h-6 text-green-600 mr-3" />
                Communication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may communicate with you via email, SMS, or WhatsApp regarding your account, bookings, and service updates.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                By providing your contact information, you consent to receive such communications.
              </p>
              <p className="text-gray-600 leading-relaxed">
                You can opt out of marketing communications at any time by contacting us or using the unsubscribe link in our emails.
              </p>
            </CardContent>
          </Card>

          {/* Prohibited Uses */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Ban className="w-6 h-6 text-red-600 mr-3" />
                Prohibited Uses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">You may not use our service:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                <li>To submit false or misleading information</li>
                <li>To upload or transmit viruses or any other type of malicious code</li>
                <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
                <li>For any obscene or immoral purpose</li>
                <li>To interfere with or circumvent the security features of the Service</li>
                  </ul>
            </CardContent>
          </Card>

          {/* Intellectual Property Rights */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Copyright className="w-6 h-6 text-purple-600 mr-3" />
                Intellectual Property Rights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service and its original content, features, and functionality are and will remain the exclusive property of Timelyyy Technologies and its licensors.
              </p>
              <p className="text-gray-600 leading-relaxed">
                The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
              </p>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                Disclaimer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, this Company:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>excludes all representations and warranties relating to this website and its contents</li>
                <li>excludes all liability for damages arising out of or in connection with your use of this website</li>
                  </ul>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
                Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                In no event shall Timelyyy Technologies, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.
              </p>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Zap className="w-6 h-6 text-yellow-600 mr-3" />
                Termination
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
              </p>
              <p className="text-gray-600 leading-relaxed">
                If you wish to terminate your account, you may simply discontinue using the Service.
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Scale className="w-6 h-6 text-indigo-600 mr-3" />
                Governing Law
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Use, along with any dispute or claim arising from or connected with them (including non-contractual disputes or claims), shall be governed by and interpreted in accordance with the laws of India. You hereby consent to the exclusive jurisdiction of the courts located in Mumbai, Maharashtra, for the resolution of any dispute or claim arising from or related to these Terms of Use.
              </p>
            </CardContent>
          </Card>

          {/* Contact Us */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Mail className="w-6 h-6 text-blue-600 mr-3" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:support@timelyyy.com"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email Us
                </a>
                <a
                  href="https://wa.me/919876543210"
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
          </div>
            </CardContent>
          </Card>
        </main>

    </div>
  );
};

export default TermsAndConditions;